/**
 * ARCTIC ARMOR™ B2B Landing Page JavaScript
 * Handles smooth scrolling, form submission, and analytics
 */

class ArcticArmorB2B {
  constructor() {
    this.init();
  }

  init() {
    this.setupSmoothScroll();
    this.setupAddToCart();
    this.setupAnalytics();
    this.setupIntersectionObserver();
  }

  /**
   * Smooth scroll for anchor links
   */
  setupSmoothScroll() {
    const smoothScrollLinks = document.querySelectorAll('.smooth-scroll');
    
    smoothScrollLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = link.getAttribute('href');
        const targetElement = document.querySelector(targetId);
        
        if (targetElement) {
          const headerOffset = 80;
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });

          // Track smooth scroll event
          this.trackEvent('Navigation', 'Smooth Scroll', targetId);
        }
      });
    });
  }

  /**
   * Add to cart functionality for bulk products
   */
  setupAddToCart() {
    const addToCartButtons = document.querySelectorAll('.ab-product-card__cta');
    
    if (addToCartButtons.length === 0) {
      console.warn('No add to cart buttons found');
      return;
    }
    
    addToCartButtons.forEach(button => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const variantId = button.getAttribute('data-variant-id');
        const productId = button.getAttribute('data-product-id');
        
        if (!variantId || variantId === '' || variantId === 'null') {
          console.error('Invalid variant ID:', variantId);
          alert('Please select a product in the theme editor first.');
          return;
        }

        const originalText = button.textContent;
        const originalBg = button.style.background;
        button.textContent = 'Adding...';
        button.disabled = true;

        try {
          // Convert to integer for Shopify API
          const variantIdInt = parseInt(variantId, 10);
          
          // Add to cart via Shopify Cart API
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: variantIdInt,
                quantity: 1
              }]
            })
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.description || data.message || 'Failed to add to cart');
          }

          // Success state
          button.textContent = '✓ Added!';
          button.style.background = '#28a745';

          // Track add to cart
          if (productId) {
            this.trackEvent('E-commerce', 'Add to Cart', productId);
          }

          // Update cart count
          this.updateCartCount();

          // Trigger cart drawer refresh
          if (window.Shopify && window.Shopify.theme) {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
              bubbles: true
            }));
          }

          // Try to open cart drawer if it exists
          const cartDrawer = document.querySelector('cart-drawer, [data-cart-drawer]');
          if (cartDrawer) {
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else {
              cartDrawer.classList.add('active', 'is-open');
            }
          }

          // Reset button after delay
          setTimeout(() => {
            button.textContent = originalText;
            button.disabled = false;
            button.style.background = originalBg;
          }, 2000);

        } catch (error) {
          console.error('Add to cart error:', error);
          
          button.textContent = 'Error - Try Again';
          button.disabled = false;
          button.style.background = '#dc3545';
          
          setTimeout(() => {
            button.textContent = originalText;
            button.style.background = originalBg;
          }, 3000);
          
          this.trackEvent('E-commerce', 'Add to Cart Error', error.message);
        }
      });
    });
  }

  /**
   * Update cart count in header
   */
  updateCartCount() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCountElements = document.querySelectorAll('.cart-count, [data-cart-count]');
        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
        });
      })
      .catch(error => console.error('Error updating cart count:', error));
  }

  /**
   * Setup analytics tracking
   */
  setupAnalytics() {
    // Track page view
    this.trackPageView();

    // Track CTA clicks
    const ctaButtons = document.querySelectorAll('.ab-btn');
    ctaButtons.forEach(button => {
      button.addEventListener('click', () => {
        const buttonText = button.textContent.trim();
        this.trackEvent('CTA', 'Click', buttonText);
      });
    });

    // Track solution card views
    this.trackSolutionViews();

    // Track scroll depth
    this.trackScrollDepth();

    // Track phone number clicks
    const phoneLinks = document.querySelectorAll('a[href^="tel:"]');
    phoneLinks.forEach(link => {
      link.addEventListener('click', () => {
        this.trackEvent('Contact', 'Phone Click', link.href);
      });
    });
  }

  /**
   * Track page view
   */
  trackPageView() {
    if (typeof gtag !== 'undefined') {
      gtag('event', 'page_view', {
        page_title: 'ARCTIC ARMOR™ B2B Landing Page',
        page_location: window.location.href,
        page_path: window.location.pathname
      });
    }

    // Shopify Analytics
    if (typeof window.ShopifyAnalytics !== 'undefined') {
      window.ShopifyAnalytics.lib.track('Viewed Landing Page', {
        page: 'ARCTIC ARMOR™ B2B'
      });
    }
  }

  /**
   * Track events
   */
  trackEvent(category, action, label = '', value = null) {
    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      const eventParams = {
        event_category: category,
        event_label: label
      };
      
      if (value !== null) {
        eventParams.value = value;
      }

      gtag('event', action, eventParams);
    }

    // Shopify Analytics
    if (typeof window.ShopifyAnalytics !== 'undefined') {
      window.ShopifyAnalytics.lib.track(action, {
        category: category,
        label: label,
        value: value
      });
    }

    // Console log for debugging
    console.log('Analytics Event:', { category, action, label, value });
  }

  /**
   * Track solution card views using Intersection Observer
   */
  trackSolutionViews() {
    const solutions = document.querySelectorAll('.ab-solution-card');
    const trackedSolutions = new Set();

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !trackedSolutions.has(entry.target)) {
          const solutionTitle = entry.target.querySelector('.ab-solution__title')?.textContent || 'Unknown';
          this.trackEvent('Engagement', 'Solution View', solutionTitle);
          trackedSolutions.add(entry.target);
        }
      });
    }, {
      threshold: 0.5
    });

    solutions.forEach(solution => observer.observe(solution));
  }

  /**
   * Track scroll depth
   */
  trackScrollDepth() {
    const milestones = [25, 50, 75, 100];
    const tracked = new Set();

    const calculateScrollPercentage = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercentage = (scrollTop / (documentHeight - windowHeight)) * 100;
      return Math.round(scrollPercentage);
    };

    const handleScroll = () => {
      const scrollPercentage = calculateScrollPercentage();

      milestones.forEach(milestone => {
        if (scrollPercentage >= milestone && !tracked.has(milestone)) {
          this.trackEvent('Engagement', 'Scroll Depth', `${milestone}%`, milestone);
          tracked.add(milestone);
        }
      });
    };

    // Throttle scroll events
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  /**
   * Setup Intersection Observer for animations
   */
  setupIntersectionObserver() {
    const animatedElements = document.querySelectorAll('.ab-benefit-card, .ab-solution-card, .ab-spec-card');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    animatedElements.forEach(element => {
      element.style.opacity = '0';
      element.style.transform = 'translateY(30px)';
      element.style.transition = 'opacity 0.6s ease-out, transform 0.6s ease-out';
      observer.observe(element);
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ArcticArmorB2B();
  });
} else {
  new ArcticArmorB2B();
}

// Handle Shopify section reloads in theme editor
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new ArcticArmorB2B();
  });
}
